// skipcq: JS-W1028
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardLayout from '../../components/layout/DashboardLayout';
import BeerDuplicateCompareView from '../../components/beer/BeerDuplicateCompareView';
import BeerDuplicateReviewQueueNav from '../../components/beer/BeerDuplicateReviewQueueNav';
import AlertWidget from '../../components/widgets/Alert';
import useBeerDuplicateReviewSession from '../../hooks/useBeerDuplicateReviewSession';
import useBeerIngestionReviewKeyboard from '../../hooks/useBeerIngestionReviewKeyboard';
import { buildBeerDuplicateInboxPath } from '../../utils/beer/beerDuplicateReviewQueue';
import {
  captureDuplicateReviewBack,
  captureDuplicateReviewNext,
  captureDuplicateReviewPrevious,
  captureDuplicateReviewView,
} from '../../utils/engageDuplicateReviewAnalytics';

const PAGE_CONTENT_SX = {
  mt: 2,
  mb: 4,
  px: { xs: 2, sm: 3 },
};

export default function BeerDuplicateReviewPage() {
  const { reviewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationQueueIds = location.state?.queueReviewIds;

  const {
    activeReview,
    loading,
    error,
    actionsLoading,
    actionsError,
    clearError,
    showQueueNav,
    neighbors,
    handleApplySelected,
    handleCompleteReview,
    handleAddBeer,
    handlePrevious,
    handleNext,
  } = useBeerDuplicateReviewSession({
    reviewId,
    globalScope: true,
    initialQueueReviewIds: locationQueueIds,
  });

  useEffect(() => {
    if (reviewId && activeReview?.breweryId) {
      captureDuplicateReviewView(reviewId, activeReview.breweryId);
    }
  }, [activeReview?.breweryId, reviewId]);

  const handleBack = useCallback(() => {
    captureDuplicateReviewBack(reviewId, activeReview?.breweryId);
    navigate(buildBeerDuplicateInboxPath());
  }, [activeReview?.breweryId, navigate, reviewId]);

  const handlePreviousWithAnalytics = useCallback(() => {
    captureDuplicateReviewPrevious(reviewId, activeReview?.breweryId);
    handlePrevious();
  }, [activeReview?.breweryId, handlePrevious, reviewId]);

  const handleNextWithAnalytics = useCallback(() => {
    captureDuplicateReviewNext(reviewId, activeReview?.breweryId);
    handleNext();
  }, [activeReview?.breweryId, handleNext, reviewId]);

  useBeerIngestionReviewKeyboard({
    enabled: showQueueNav,
    onPrevious: handlePreviousWithAnalytics,
    onNext: handleNextWithAnalytics,
  });

  const handleCloseError = useCallback(() => {
    clearError();
  }, [clearError]);

  const wrappedApply = useCallback(async (selectedFields) => {
    const result = await handleApplySelected(selectedFields);
    return result;
  }, [handleApplySelected]);

  const wrappedComplete = useCallback(async () => {
    await handleCompleteReview();
  }, [handleCompleteReview]);

  const wrappedAddBeer = useCallback(async () => {
    await handleAddBeer();
  }, [handleAddBeer]);

  return (
    <DashboardLayout title="Beer duplicate review">
      <Box sx={PAGE_CONTENT_SX}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 1 }}>
          Back to duplicate inbox
        </Button>
        <BeerDuplicateReviewQueueNav
          showQueueNav={showQueueNav}
          previousDisabled={!neighbors.previousReviewId}
          nextDisabled={!neighbors.nextReviewId}
          positionLabel={neighbors.positionLabel}
          onPrevious={handlePreviousWithAnalytics}
          onNext={handleNextWithAnalytics}
        />
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} aria-label="Loading duplicate review" />
          </Box>
        ) : null}
        {!loading && activeReview ? (
          <BeerDuplicateCompareView
            reviewRow={activeReview}
            actionsDisabled={actionsLoading}
            onApplySelected={wrappedApply}
            onCompleteReview={wrappedComplete}
            onAddBeer={wrappedAddBeer}
          />
        ) : null}
        {!loading && !activeReview && !error ? (
          <Alert severity="warning">Duplicate review not found in the pending queue.</Alert>
        ) : null}
        <Snackbar open={Boolean(actionsError)} autoHideDuration={6000} onClose={handleCloseError}>
          <AlertWidget onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
            {actionsError}
          </AlertWidget>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}

BeerDuplicateReviewPage.propTypes = {};
